<template>
  <div
    v-if="isVisible"
    class="mini-cart__cart-addon border-b py-4"
  >
    <div class="mini-cart__cart-addon-row flex items-start justify-between gap-4">
      <div class="mini-cart__cart-addon-details flex gap-3 min-w-0">
        <span
          class="mini-cart__cart-addon-icon shrink-0"
          aria-hidden="true"
        >
          <MiniCartIconLeaf />
        </span>

        <div class="mini-cart__cart-addon-copy min-w-0">
          <p class="mini-cart__cart-addon-title font-bold uppercase text-xs tracking-wide">
            {{ config.title }}
          </p>
          <p class="mini-cart__cart-addon-description text-xs mt-1 flex items-center gap-1 flex-wrap">
            <span>{{ config.description }}</span>
            <button
              v-if="packageProtectionDialogId"
              type="button"
              class="mini-cart__cart-addon-info inline-flex items-center"
              :aria-label="config.infoAriaLabel"
              :aria-controls="packageProtectionDialogId"
              @click="openPackageProtectionDialog"
            >
              <MiniCartIconInfoCircle />
            </button>
          </p>
        </div>
      </div>

      <div class="mini-cart__cart-addon-pricing shrink-0 text-right text-xs">
        <span class="font-bold uppercase">{{ displayPriceLabel }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import formatMoney from '../helpers/format-money.js';
import MiniCartIconLeaf from './mini-cart--icon-leaf.vue';
import MiniCartIconInfoCircle from './mini-cart--icon-info-circle.vue';

const OPT_OUT_KEY = 'cart_addon_opted_out';

const props = defineProps({
  cartData: {
    type: Object,
    required: true,
  },
  hasRegularItems: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['cart-updated']);

const config = computed(() => window.cartAddonConfig || {});
const packageProtectionDialogId = computed(() => config.value.packageProtectionDialogId || '');
const addonLineItem = ref(null);
const isSyncing = ref(false);

const isEnabled = computed(() => Boolean(config.value.enabled && config.value.product?.id));
const isVisible = computed(() => isEnabled.value && props.hasRegularItems && !hasOptedOut());

const displayPriceLabel = computed(() => {
  if (config.value.displayPriceLabel) return config.value.displayPriceLabel;

  const lineItem = addonLineItem.value;
  if (!lineItem) return config.value.freeLabel || 'FREE';

  const finalPrice = lineItem.final_line_price ?? lineItem.line_price ?? lineItem.price;
  return finalPrice === 0 ? (config.value.freeLabel || 'FREE') : formatMoney(finalPrice);
});

const strikethroughPrice = computed(() => {
  if (addonLineItem.value?.properties?._compare_at_price) {
    return Number(addonLineItem.value.properties._compare_at_price);
  }

  return config.value.product?.compareAtPrice || config.value.product?.price || 0;
});

const showStrikethroughPrice = computed(() => {
  if (!config.value.showStrikethroughPrice) return false;
  return strikethroughPrice.value > 0;
});

const isCartAddonItem = (item) => {
  return item?.properties?._cart_addon === true || item?.properties?._cart_addon === 'true';
};

const isFreeSampleItem = (item) => {
  return item?.properties?._sample === true || item?.properties?._sample === 'true';
};

const hasOptedOut = () => sessionStorage.getItem(OPT_OUT_KEY) === 'true';

const setOptedOut = (value) => {
  if (value) {
    sessionStorage.setItem(OPT_OUT_KEY, 'true');
  } else {
    sessionStorage.removeItem(OPT_OUT_KEY);
  }
};

const refreshAddonLineItem = (cart) => {
  addonLineItem.value = cart?.items?.find(isCartAddonItem) || null;
};

const fetchCart = async () => {
  const response = await fetch(`${window.Shopify.routes.root}cart.js`);
  if (!response.ok) throw new Error('Failed to fetch cart');
  return response.json();
};

const addAddonToCart = async () => {
  const variantId = config.value.product?.variantId;
  if (!variantId) return;

  const properties = {
    _cart_addon: 'true',
  };

  if (config.value.product?.compareAtPrice) {
    properties._compare_at_price = config.value.product.compareAtPrice;
  }

  const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify({
      id: variantId,
      quantity: 1,
      properties,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.description || 'Failed to add cart add-on');
  }
};

const removeAddonFromCart = async (lineItem) => {
  const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify({
      id: lineItem.key,
      quantity: 0,
    }),
  });

  if (!response.ok) throw new Error('Failed to remove cart add-on');
};

const syncCartAddon = async (cart = props.cartData) => {
  if (!isEnabled.value || isSyncing.value || !cart?.items) return;

  isSyncing.value = true;

  try {
    refreshAddonLineItem(cart);

    const regularItemsExist = cart.items?.some(
      (item) => !isCartAddonItem(item) && !isFreeSampleItem(item)
    );

    if (!regularItemsExist) {
      if (addonLineItem.value) {
        await removeAddonFromCart(addonLineItem.value);
        const updatedCart = await fetchCart();
        refreshAddonLineItem(updatedCart);
        emit('cart-updated', updatedCart);
      }
      return;
    }

    if (hasOptedOut()) {
      if (addonLineItem.value) {
        await removeAddonFromCart(addonLineItem.value);
        const updatedCart = await fetchCart();
        refreshAddonLineItem(updatedCart);
        emit('cart-updated', updatedCart);
      }
      return;
    }

    if (!addonLineItem.value) {
      await addAddonToCart();
      const updatedCart = await fetchCart();
      refreshAddonLineItem(updatedCart);
      emit('cart-updated', updatedCart);
    }
  } catch (error) {
    console.error('Error syncing cart add-on:', error);
  } finally {
    isSyncing.value = false;
  }
};

const optOutAndCheckout = async () => {
  setOptedOut(true);

  try {
    isSyncing.value = true;
    const cart = await fetchCart();
    refreshAddonLineItem(cart);

    if (addonLineItem.value) {
      await removeAddonFromCart(addonLineItem.value);
    }

    window.location.href = `${window.Shopify.routes.root}checkout`;
  } catch (error) {
    console.error('Error opting out of cart add-on:', error);
    isSyncing.value = false;
  }
};

const openPackageProtectionDialog = () => {
  const dialog = document.getElementById(packageProtectionDialogId.value);
  dialog?.showModal();
};

watch(
  () => props.cartData,
  (cart) => {
    refreshAddonLineItem(cart);
    syncCartAddon(cart);
  },
  { deep: true }
);

watch(
  () => props.hasRegularItems,
  (hasRegularItems) => {
    if (!hasRegularItems) {
      addonLineItem.value = null;
    }
  }
);

onMounted(() => {
  refreshAddonLineItem(props.cartData);
  syncCartAddon(props.cartData);
});

defineExpose({
  isCartAddonItem,
  syncCartAddon,
  setOptedOut,
  optOutAndCheckout,
});
</script>
